import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HashtagSuggestion } from '../../../../shared/services/hashtag.service';

/**
 * HashtagDropdownComponent
 *
 * Renders the autocomplete dropdown for hashtag suggestions.
 * Mirrors the design of MentionDropdownComponent so both feel consistent.
 *
 * Usage:
 *   <app-hashtag-dropdown
 *     [isOpen]="hashDropOpen"
 *     [tags]="hashSuggestions"
 *     (selected)="onHashtagSelected($event)">
 *   </app-hashtag-dropdown>
 */
@Component({
  selector:        'app-hashtag-dropdown',
  standalone:      true,
  imports:         [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (isOpen && tags.length > 0) {
      <div class="absolute left-0 right-0 z-50 bg-white rounded-xl border border-gray-200
                  shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark overflow-hidden"
           [ngClass]="position === 'below' ? 'top-full mt-1' : 'bottom-full mb-1'">

        <!-- Header -->
        <div class="px-3 py-2 text-[11px] font-semibold text-gray-400
                    uppercase tracking-wide border-b border-gray-100
                    dark:border-gray-800 flex items-center gap-1.5">
          <!-- # icon -->
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2.5"
               stroke-linecap="round" stroke-linejoin="round">
            <line x1="4"  y1="9"  x2="20" y2="9"/>
            <line x1="4"  y1="15" x2="20" y2="15"/>
            <line x1="10" y1="3"  x2="8"  y2="21"/>
            <line x1="16" y1="3"  x2="14" y2="21"/>
          </svg>
          Hashtags
        </div>

        <!-- Tag list -->
        <div class="max-h-52 overflow-y-auto">
          <button *ngFor="let t of tags; trackBy: trackByTag"
                  type="button"
                  (mousedown)="pick(t)"
                  class="flex w-full items-center gap-3 px-3 py-2.5
                         hover:bg-[#fff5f7] dark:hover:bg-gray-800
                         transition-colors text-left group">

            <!-- Hash icon bubble -->
            <div class="w-9 h-9 rounded-full bg-[#FFD4E0] flex items-center
                        justify-center text-[#D31148] font-bold text-base flex-shrink-0
                        group-hover:bg-[#D31148] group-hover:text-white transition-colors">
              #
            </div>

            <!-- Tag name + usage count -->
            <div class="flex-1 min-w-0">
              <div class="text-sm font-semibold text-gray-800 dark:text-gray-100
                          truncate group-hover:text-[#D31148] transition-colors">
                #{{ t.tag }}
              </div>
              <div class="text-xs text-gray-400">
                {{ t.usageCount | number }} {{ t.usageCount === 1 ? 'post' : 'posts' }}
              </div>
            </div>

          </button>
        </div>

      </div>
    }
  `,
})
export class HashtagDropdownComponent {
  @Input()  isOpen   = false;
  @Input()  tags:     HashtagSuggestion[] = [];
  @Input()  position: 'above' | 'below'  = 'below';
  @Output() selected = new EventEmitter<HashtagSuggestion>();

  pick(tag: HashtagSuggestion): void {
    this.selected.emit(tag);
  }

  trackByTag(_: number, t: HashtagSuggestion): string {
    return t.tag;
  }
}